import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Pagination,
  Collapse,
  Table,
  Tag,
  Drawer,
  Input,
  Select,
  DatePicker,
  Space,
  Button,
  Modal,
  message,
  Empty,
  Tooltip,
} from "antd";
import {
  CaretRightOutlined,
  PlusOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  TransactionOutlined,
  UnorderedListOutlined,
  ExclamationCircleOutlined,
  BankOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import axios from "axios";
import PropTypes from "prop-types";
import { formatDateTime } from "../../helpers/date";
import {
  formatAmount,
  pivotChargesByConsignment,
  chargeColumns,
  downloadStatementPdf,
  downloadStatementExcel,
  getDisplayStatus,
  roundToCents,
  DISPLAY_STATUS_COLORS,
  DISPLAY_STATUS_LABELS,
} from "../../helpers/billingStatement";
import { BILLING_STATEMENT_STATUSES, PAYABLE_STATUSES } from "../../constants/billingStatement";
import { BILLING_TYPE_LABELS } from "../../constants/billing";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";
import BillingBankTransferModal from "./BillingBankTransferModal";

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

const SEARCH_FIELD_PLACEHOLDERS = {
  userEmail: "Search by merchant account email",
  statementNumber: "Search by invoice no.",
  consignNo: "Search by tracking no.",
};

const BillingStatement = ({ onViewTransactions, onViewCharges, onTransferRecorded }) => {
  const { permissions } = usePermissions();
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 20 });
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [statementCharges, setStatementCharges] = useState([]);
  const [chargesLoading, setChargesLoading] = useState(false);

  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateEmail, setGenerateEmail] = useState("");
  const [generatePeriod, setGeneratePeriod] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Bulk archive + unbill. Irreversible hard delete on the API side (BillingStatementRepository.archive)
  // -- gated only by GENERATE_BILLING_STATEMENT, no separate feature flag, so it confirms before firing.
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [archiving, setArchiving] = useState(false);

  const [transferStatement, setTransferStatement] = useState(null);

  const buildFilterParams = useCallback(
    () => ({
      ...(filters.search && { search: filters.search, searchField: filters.searchField || "userEmail" }),
      ...(filters.status && { status: filters.status }),
      ...(filters.billingPeriod && { billingPeriod: filters.billingPeriod }),
    }),
    [filters],
  );

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...buildFilterParams(),
        page: pagination.currentPage,
        limit: pagination.limit,
      };
      const res = await axios.get("/api/billing-statements", { params });
      setItems(res.data.data);
      setPagination((prev) => ({ ...prev, totalSize: res.data.pagination?.totalSize }));
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, buildFilterParams]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (!selected) {
      setStatementCharges([]);
      return;
    }

    setChargesLoading(true);
    axios
      .get(`/api/billing-statements/${selected.objectId}/charges`)
      .then((res) => setStatementCharges(pivotChargesByConsignment(res.data.data)))
      .catch((error) => message.error(error.response?.data?.message || error.message))
      .finally(() => setChargesLoading(false));
  }, [selected]);

  const handlePageChange = (currentPage, limit) => {
    setPagination((prev) => ({ ...prev, currentPage, limit }));
  };

  const handleFilterChange = (updater) => {
    setFilters(updater);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const [filterResetKey, setFilterResetKey] = useState(0);
  // searchField is excluded on purpose -- it's just the paired dropdown for "search", not an
  // independent filter, so picking "Invoice No." with an empty search box shouldn't count as 1.
  const activeFilterCount = [filters.search, filters.status, filters.billingPeriod].filter(Boolean).length;
  const clearAllFilters = () => {
    setFilters({});
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setFilterResetKey((k) => k + 1); // remounts the (uncontrolled) filter inputs so they visually clear too
  };

  const searchDebounce = useRef(null);
  useEffect(() => () => clearTimeout(searchDebounce.current), []);
  const handleSearchChange = (e) => {
    const value = e.target.value.trim() || undefined;
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      handleFilterChange((f) => ({ ...f, search: value }));
    }, 400);
  };

  const handleGenerate = async () => {
    if (!generateEmail || !generatePeriod) {
      message.warning("Merchant email and billing period are required");
      return;
    }

    setGenerating(true);
    try {
      await axios.post("/api/billing-statements/generate", {
        merchantEmail: generateEmail,
        billingPeriod: generatePeriod.format("YYYY-MM"),
      });
      message.success("Statement generated");
      setGenerateOpen(false);
      setGenerateEmail("");
      setGeneratePeriod(null);
      fetchItems();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleArchive = () => {
    if (selectedRowKeys.length === 0) return;

    Modal.confirm({
      title: `Archive ${selectedRowKeys.length} statement(s)?`,
      icon: <ExclamationCircleOutlined />,
      content:
        "This permanently deletes the selected statements and reverts their charges back to unbilled. This cannot be undone.",
      okText: "Archive",
      okButtonProps: { danger: true },
      onOk: async () => {
        setArchiving(true);
        try {
          await axios.post("/api/billing-statements/archive", { ids: selectedRowKeys });
          message.success(`${selectedRowKeys.length} statement(s) archived`);
          setSelectedRowKeys([]);
          fetchItems();
        } catch (error) {
          message.error(error.response?.data?.message || error.message);
        } finally {
          setArchiving(false);
        }
      },
    });
  };

  const handleUnblock = (record) => {
    Modal.confirm({
      title: `Unblock ${record.merchant?.name || "this merchant"}?`,
      icon: <ExclamationCircleOutlined />,
      content:
        "This lifts the billing block on the merchant's account. Recording a bank transfer does not do this automatically, so only unblock once Finance has verified the payment. It is refused while any overdue statement still has money owed.",
      okText: "Unblock",
      onOk: async () => {
        try {
          await axios.post(`/api/merchants/${record.merchant.objectId}/billing-unblock`);
          message.success("Merchant unblocked");
          fetchItems();
        } catch (error) {
          message.error(error.response?.data?.message || error.message);
        }
      },
    });
  };

  const canRecordTransfer = (record) =>
    PAYABLE_STATUSES.includes(record.status) && roundToCents(record.outstandingAmount) > 0;

  const columns = [
    {
      title: "Statement No.",
      dataIndex: "statementNumber",
      key: "statementNumber",
      width: 160,
      render: (v) => <span style={{ fontWeight: 500 }}>{v}</span>,
    },
    {
      title: "Merchant",
      key: "merchant",
      width: 200,
      ellipsis: true,
      render: (_, r) => r.merchant?.name || r.merchant?.objectId || "-",
    },
    {
      title: "User Email",
      dataIndex: "userEmail",
      key: "userEmail",
      width: 220,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Billing Period",
      dataIndex: "billingPeriod",
      key: "billingPeriod",
      width: 120,
    },
    {
      title: "Billing Type",
      dataIndex: "billingType",
      key: "billingType",
      width: 180,
      render: (t) => BILLING_TYPE_LABELS[t] || t || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (_, record) => {
        const displayStatus = getDisplayStatus(record);
        return <Tag color={DISPLAY_STATUS_COLORS[displayStatus]}>{DISPLAY_STATUS_LABELS[displayStatus]}</Tag>;
      },
    },
    {
      title: "Issued",
      dataIndex: "issuedAt",
      key: "issuedAt",
      width: 160,
      render: (t) => (t ? formatDateTime(t) : "-"),
    },
    {
      title: "Due",
      dataIndex: "dueAt",
      key: "dueAt",
      width: 160,
      render: (t) => (t ? formatDateTime(t) : "-"),
    },
    {
      title: "Original Amount",
      key: "originalAmount",
      width: 150,
      align: "right",
      render: (_, r) => formatAmount(r.originalAmount, r.currency),
    },
    {
      title: "Outstanding",
      key: "outstandingAmount",
      width: 150,
      align: "right",
      render: (_, r) => (
        <span style={{ fontWeight: 500, color: Number(r.outstandingAmount) > 0 ? "#cf1322" : undefined }}>
          {formatAmount(r.outstandingAmount, r.currency)}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 235,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="View">
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => setSelected(record)} />
          </Tooltip>
          <Tooltip title="Download PDF">
            <Button
              type="text"
              size="small"
              icon={<FilePdfOutlined />}
              onClick={() => downloadStatementPdf(record.objectId)}
            />
          </Tooltip>
          <Tooltip title="Download Excel">
            <Button
              type="text"
              size="small"
              icon={<FileExcelOutlined />}
              onClick={() => downloadStatementExcel(record.objectId)}
            />
          </Tooltip>
          {/* Unlike Transactions, charges are pinned to a statement at generation time, not
              payment time -- so this is available regardless of status. */}
          {onViewCharges && (
            <Tooltip title="View Charges">
              <Button
                type="text"
                size="small"
                icon={<UnorderedListOutlined />}
                onClick={() => onViewCharges(record.objectId)}
              />
            </Tooltip>
          )}
          {permissions?.includes(PERMISSIONS.RECORD_BANK_TRANSFER) && canRecordTransfer(record) && (
            <Tooltip title="Record bank transfer">
              <Button type="text" size="small" icon={<BankOutlined />} onClick={() => setTransferStatement(record)} />
            </Tooltip>
          )}
          {permissions?.includes(PERMISSIONS.UNBLOCK_BILLING_ACCOUNT) && record.merchant?.billingBlockedAt && (
            <Tooltip title="Unblock merchant">
              <Button type="text" size="small" icon={<UnlockOutlined />} onClick={() => handleUnblock(record)} />
            </Tooltip>
          )}
          {/* Partial payment is supported, so any status can have transactions. */}
          {onViewTransactions && (
            <Tooltip title="View Transactions">
              <Button
                type="text"
                size="small"
                icon={<TransactionOutlined />}
                onClick={() => onViewTransactions(record.objectId)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {permissions?.includes(PERMISSIONS.GENERATE_BILLING_STATEMENT) && (
        <Row justify="end" style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setGenerateOpen(true)}>
            Generate Statement
          </Button>
        </Row>
      )}

      <Collapse
        defaultActiveKey={activeFilterCount > 0 ? ["filters"] : undefined}
        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
      >
        <Collapse.Panel
          key="filters"
          header={
            <Space>
              <span>Filters</span>
              {activeFilterCount > 0 && <Tag color="blue">{activeFilterCount} active</Tag>}
            </Space>
          }
          extra={
            activeFilterCount > 0 && (
              <a onClick={(e) => { e.stopPropagation(); clearAllFilters(); }}>Clear all</a>
            )
          }
        >
          <Row gutter={[16, 16]} key={filterResetKey}>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>Search</span>
                <Input
                  allowClear
                  defaultValue={filters.search}
                  placeholder={SEARCH_FIELD_PLACEHOLDERS[filters.searchField || "userEmail"]}
                  onChange={handleSearchChange}
                  addonBefore={
                    <Select
                      defaultValue={filters.searchField || "userEmail"}
                      style={{ width: 130 }}
                      onChange={(v) => handleFilterChange((f) => ({ ...f, searchField: v }))}
                    >
                      <Select.Option value="userEmail">Email</Select.Option>
                      <Select.Option value="statementNumber">Invoice No.</Select.Option>
                      <Select.Option value="consignNo">Tracking No.</Select.Option>
                    </Select>
                  }
                />
              </Space>
            </Col>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>Status</span>
                <Select
                  allowClear
                  defaultValue={filters.status}
                  placeholder="ALL"
                  style={{ width: "100%" }}
                  onChange={(v) => handleFilterChange((f) => ({ ...f, status: v }))}
                >
                  {BILLING_STATEMENT_STATUSES.map((status) => (
                    <Select.Option key={status} value={status}>
                      {status}
                    </Select.Option>
                  ))}
                </Select>
              </Space>
            </Col>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>Billing Period</span>
                <DatePicker
                  picker="month"
                  allowClear
                  style={{ width: "100%" }}
                  onChange={(v) =>
                    handleFilterChange((f) => ({
                      ...f,
                      billingPeriod: v ? v.format("YYYY-MM") : undefined,
                    }))
                  }
                />
              </Space>
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>

      <Row justify="end" style={{ marginTop: 16 }}>
        <Button
          danger
          disabled={selectedRowKeys.length === 0}
          loading={archiving}
          onClick={handleArchive}
        >
          Archive Selected ({selectedRowKeys.length})
        </Button>
      </Row>

      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={items}
        pagination={false}
        loading={loading}
        scroll={{ x: true }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={activeFilterCount > 0 ? "No statements match these filters" : "No billing statements yet"}
              style={{ padding: "24px 0" }}
            >
              {activeFilterCount > 0 ? (
                <a onClick={clearAllFilters}>Clear filters</a>
              ) : (
                permissions?.includes(PERMISSIONS.GENERATE_BILLING_STATEMENT) && (
                  <Button type="primary" size="small" onClick={() => setGenerateOpen(true)}>
                    Generate Statement
                  </Button>
                )
              )}
            </Empty>
          ),
        }}
        style={{ marginTop: 16 }}
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
      />

      <Row justify="end" style={{ marginTop: 16 }}>
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination.limit}
            current={pagination.currentPage}
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onChange={handlePageChange}
          />
        </Col>
      </Row>

      <Drawer
        title="Billing statement detail"
        visible={!!selected}
        onClose={() => setSelected(null)}
        width={960}
      >
        {selected && (
          <div>
            <Space style={{ marginBottom: 16 }}>
              <Button type="primary" onClick={() => downloadStatementPdf(selected.objectId)}>
                Download PDF
              </Button>
              <Button onClick={() => downloadStatementExcel(selected.objectId)}>
                Download Excel
              </Button>
            </Space>
            <p><strong>Statement No.:</strong> {selected.statementNumber}</p>
            <p><strong>Merchant:</strong> {selected.merchant?.name || selected.merchant?.objectId}</p>
            <p><strong>User Email:</strong> {selected.userEmail || "-"}</p>
            <p><strong>Billing Period:</strong> {selected.billingPeriod}</p>
            <p><strong>Period:</strong> {formatDateTime(selected.periodStart)} - {formatDateTime(selected.periodEnd)}</p>
            <p><strong>Billing Type:</strong> {BILLING_TYPE_LABELS[selected.billingType] || selected.billingType}</p>
            <p><strong>Credit Term:</strong> {selected.creditTerm} days</p>
            <p>
              <strong>Status:</strong>{" "}
              <Tag color={DISPLAY_STATUS_COLORS[getDisplayStatus(selected)]}>
                {DISPLAY_STATUS_LABELS[getDisplayStatus(selected)]}
              </Tag>
            </p>
            <p><strong>Issued At:</strong> {selected.issuedAt ? formatDateTime(selected.issuedAt) : "-"}</p>
            <p><strong>Due At:</strong> {selected.dueAt ? formatDateTime(selected.dueAt) : "-"}</p>
            <p><strong>Original Amount:</strong> {formatAmount(selected.originalAmount, selected.currency)}</p>
            <p><strong>Paid Amount:</strong> {formatAmount(selected.paidAmount, selected.currency)}</p>
            <p><strong>Offset Amount:</strong> {formatAmount(selected.offsetAmount, selected.currency)}</p>
            <p><strong>Outstanding Amount:</strong> {formatAmount(selected.outstandingAmount, selected.currency)}</p>
            <p><strong>Line Count:</strong> {selected.lineCount}</p>
            {selected.finalisedAt && <p><strong>Finalised At:</strong> {formatDateTime(selected.finalisedAt)}</p>}
            {selected.paidAt && <p><strong>Paid At:</strong> {formatDateTime(selected.paidAt)}</p>}

            <h4 style={{ marginTop: 24 }}>
              <strong>Itemised Charges</strong>
            </h4>
            <Table
              rowKey="consignNo"
              columns={chargeColumns}
              dataSource={statementCharges}
              loading={chargesLoading}
              pagination={false}
              scroll={{ x: true }}
              size="small"
              locale={{ emptyText: "No line items" }}
            />
          </div>
        )}
      </Drawer>

      <BillingBankTransferModal
        statement={transferStatement}
        onClose={() => setTransferStatement(null)}
        onRecorded={() => {
          setTransferStatement(null);
          fetchItems();
          if (onTransferRecorded) onTransferRecorded();
        }}
      />

      <Modal
        title="Generate Billing Statement"
        visible={generateOpen}
        onCancel={() => setGenerateOpen(false)}
        onOk={handleGenerate}
        confirmLoading={generating}
        okText="Generate"
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <div>Merchant Email</div>
            <Input
              placeholder="merchant@example.com"
              value={generateEmail}
              onChange={(e) => setGenerateEmail(e.target.value.trim())}
            />
          </div>
          <div>
            <div>Billing Period</div>
            <DatePicker
              picker="month"
              style={{ width: "100%" }}
              value={generatePeriod}
              onChange={setGeneratePeriod}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

BillingStatement.propTypes = {
  onViewTransactions: PropTypes.func,
  onViewCharges: PropTypes.func,
  onTransferRecorded: PropTypes.func,
};

export default BillingStatement;
